import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HiPlus, HiSearch, HiEye, HiTrash,
  HiPencilAlt, HiX,
} from 'react-icons/hi';
import toast from 'react-hot-toast';
import Layout from '../components/Layout';
import { supabase } from '../supabase/client';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../utils/format';

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_STYLE = {
  Draft:  'bg-gray-100   text-gray-600',
  Sent:   'bg-blue-100   text-blue-700',
  Signed: 'bg-green-100  text-green-700',
};

// ─── Delete Modal ─────────────────────────────────────────────────────────────

function DeleteModal({ contract, onClose, onConfirm, deleting }) {
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl border border-gray-100 p-6 text-center">
        <div className="w-14 h-14 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <HiTrash className="w-7 h-7 text-red-500" />
        </div>
        <h3 className="text-base font-bold text-gray-900 mb-2">Delete Contract</h3>
        <p className="text-sm text-gray-500 leading-relaxed">
          Delete <span className="font-semibold text-gray-800">{contract.title}</span>?
          <br />This cannot be undone.
        </p>
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 rounded-xl transition"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className="flex-1 px-4 py-2.5 text-sm font-semibold bg-red-500 hover:bg-red-600 text-white rounded-xl transition disabled:opacity-60"
          >
            {deleting ? 'Deleting…' : 'Yes, Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Skeleton row ─────────────────────────────────────────────────────────────

function SkeletonRow() {
  return (
    <div className="flex items-center gap-4 px-6 py-4 animate-pulse">
      <div className="flex-1 space-y-2">
        <div className="h-4 bg-gray-200 rounded w-1/3" />
        <div className="h-3 bg-gray-100 rounded w-1/4" />
      </div>
      <div className="h-5 bg-gray-100 rounded-full w-14 hidden sm:block" />
      <div className="h-3 bg-gray-100 rounded w-20 hidden md:block" />
      <div className="flex gap-1">
        <div className="w-8 h-8 bg-gray-100 rounded-lg" />
        <div className="w-8 h-8 bg-gray-100 rounded-lg" />
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function Contracts() {
  const { user }   = useAuth();
  const navigate   = useNavigate();
  const [contracts,    setContracts]    = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting,     setDeleting]     = useState(false);

  const fetchContracts = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('contracts')
      .select('*, clients(name)')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) toast.error('Failed to load contracts');
    else setContracts(data ?? []);
    setLoading(false);
  }, [user.id]);

  useEffect(() => { fetchContracts(); }, [fetchContracts]);

  const handleDelete = async () => {
    setDeleting(true);
    const { error } = await supabase
      .from('contracts')
      .delete()
      .eq('id', deleteTarget.id)
      .eq('user_id', user.id);

    if (error) {
      toast.error(error.message);
    } else {
      toast.success('Contract deleted');
      setContracts((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      setDeleteTarget(null);
    }
    setDeleting(false);
  };

  const q = search.toLowerCase();
  const filtered = contracts.filter((c) =>
    c.title.toLowerCase().includes(q) ||
    (c.clients?.name ?? '').toLowerCase().includes(q)
  );

  return (
    <Layout title="Contracts">

      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Contracts</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {loading ? 'Loading…' : `${contracts.length} contract${contracts.length !== 1 ? 's' : ''} total`}
          </p>
        </div>
        <button
          onClick={() => navigate('/contracts/new')}
          className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white
            text-sm font-semibold px-4 py-2.5 rounded-xl transition shadow-sm self-start sm:self-auto"
        >
          <HiPlus className="w-4 h-4" /> Create Contract
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-5">
        <HiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by title or client…"
          className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm
            text-gray-900 placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2
            focus:ring-indigo-500 focus:border-transparent"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <HiX className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Table card */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">

        {/* Column headers — md+ */}
        {!loading && filtered.length > 0 && (
          <div className="hidden md:grid px-6 py-3 border-b border-gray-50 bg-gray-50/80
            grid-cols-[minmax(0,2fr)_minmax(0,1fr)_100px_120px_120px_80px] gap-4">
            {['Title', 'Client', 'Status', 'Created', 'Signed', 'Actions'].map((h) => (
              <p key={h} className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{h}</p>
            ))}
          </div>
        )}

        {loading ? (
          <div className="divide-y divide-gray-50">
            {[...Array(4)].map((_, i) => <SkeletonRow key={i} />)}
          </div>

        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
            <div className="w-16 h-16 bg-indigo-50 rounded-2xl flex items-center justify-center mb-4">
              <HiPencilAlt className="w-8 h-8 text-indigo-400" />
            </div>
            {search ? (
              <>
                <p className="text-sm font-semibold text-gray-700">No results for "{search}"</p>
                <button onClick={() => setSearch('')} className="mt-3 text-xs text-indigo-600 font-medium hover:underline">
                  Clear search
                </button>
              </>
            ) : (
              <>
                <p className="text-sm font-semibold text-gray-700">No contracts yet</p>
                <p className="text-xs text-gray-400 mt-1">Create your first contract to get started</p>
                <button
                  onClick={() => navigate('/contracts/new')}
                  className="mt-4 inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700
                    text-white text-xs font-semibold px-4 py-2 rounded-xl transition"
                >
                  <HiPlus className="w-3.5 h-3.5" /> Create contract
                </button>
              </>
            )}
          </div>

        ) : (
          <div className="divide-y divide-gray-50">
            {filtered.map((contract) => (
              <div
                key={contract.id}
                className="flex md:grid md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_100px_120px_120px_80px]
                  gap-2 md:gap-4 items-center px-6 py-4 hover:bg-gray-50/70 transition group"
              >
                {/* Title + client (mobile stacked) */}
                <div className="min-w-0 flex-1">
                  <button
                    onClick={() => navigate(`/contracts/${contract.id}`)}
                    className="text-sm font-semibold text-gray-900 hover:text-indigo-600 transition text-left truncate block"
                  >
                    {contract.title}
                  </button>
                  <p className="text-xs text-gray-400 mt-0.5 truncate md:hidden">
                    {contract.clients?.name ?? 'No client'}
                  </p>
                </div>

                {/* Client — desktop */}
                <p className="text-sm text-gray-600 truncate hidden md:block">
                  {contract.clients?.name ?? <span className="text-gray-300">—</span>}
                </p>

                {/* Status */}
                <div className="flex-shrink-0">
                  <span className={`inline-flex text-xs font-semibold px-2.5 py-1 rounded-full
                    ${STATUS_STYLE[contract.status] ?? 'bg-gray-100 text-gray-500'}`}>
                    {contract.status}
                  </span>
                </div>

                {/* Created — desktop */}
                <p className="text-xs text-gray-400 hidden md:block">
                  {formatDate(contract.created_at)}
                </p>

                {/* Signed date — desktop */}
                <p className="text-xs text-gray-400 hidden md:block">
                  {contract.signed_at ? formatDate(contract.signed_at) : '—'}
                </p>

                {/* Actions */}
                <div className="flex items-center gap-1 flex-shrink-0
                  md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => navigate(`/contracts/${contract.id}`)}
                    title="View"
                    className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                  >
                    <HiEye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setDeleteTarget(contract)}
                    title="Delete"
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                  >
                    <HiTrash className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {deleteTarget && (
        <DeleteModal
          contract={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          deleting={deleting}
        />
      )}
    </Layout>
  );
}
